import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  getPendingReviews, 
  approveReview, 
  rejectReview,
  IMAGE_BASE_URL
} from '../../services/api';
import { 
  FiEye,
  FiStar,
  FiCheckCircle,
  FiXCircle,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiAlertCircle,
  FiClock,
  FiUser,
  FiPackage,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiMessageSquare,
  FiThumbsUp,
  FiThumbsDown,
  FiGrid,
  FiBarChart2,
  FiEdit,
  FiMoreVertical,
  FiX,
  FiCheck,
  FiTrendingUp,
  FiTrendingDown
} from 'react-icons/fi';

function Reviews({ admin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusFromUrl = queryParams.get('status');
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkAction, setBulkAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reviewToReject, setReviewToReject] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Initialize filters with status from URL
  const [filters, setFilters] = useState({
    status: statusFromUrl || '',
    rating: '',
    dateRange: 'all',
    page: 1,
    limit: 20
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });

  const statusConfig = {
    pending: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <FiClock />, 
      label: 'Pending' 
    },
    approved: { 
      color: 'text-green-400', 
      bg: 'bg-green-900/30', 
      border: 'border-green-800/50', 
      icon: <FiCheckCircle />, 
      label: 'Approved' 
    },
    rejected: { 
      color: 'text-red-400', 
      bg: 'bg-red-900/30', 
      border: 'border-red-800/50', 
      icon: <FiXCircle />, 
      label: 'Rejected' 
    }
  };

  useEffect(() => {
    const newStatus = queryParams.get('status') || '';
    if (newStatus !== filters.status) {
      setFilters(prev => ({ ...prev, status: newStatus }));
    }
  }, [location.search]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: filters.page,
        limit: filters.limit,
        rating: filters.rating || undefined,
        dateRange: filters.dateRange !== 'all' ? filters.dateRange : undefined,
        search: searchTerm || undefined
      };
      
      if (filters.status) {
        params.status = filters.status;
      }
      
      const response = await getPendingReviews(params);
      
      if (response.success && response.data) {
        setReviews(response.data.reviews || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 1
        });
      } else {
        setReviews(response.reviews || []);
        setPagination(response.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 1
        });
      }
    } catch (error) {
      setError('Failed to fetch reviews: ' + error.message);
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [filters.page, filters.status, filters.rating, filters.dateRange, searchTerm]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    
    // Update URL
    const searchParams = new URLSearchParams();
    
    if (newFilters.status) {
      searchParams.set('status', newFilters.status);
    }
    
    if (newFilters.rating) {
      searchParams.set('rating', newFilters.rating);
    }
    
    if (newFilters.dateRange && newFilters.dateRange !== 'all') {
      searchParams.set('date_range', newFilters.dateRange);
    }
    
    navigate({ search: searchParams.toString() });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReviews();
  };

  const handleApprove = async (reviewId) => {
    try {
      setActionLoading(reviewId);
      setError('');
      await approveReview(reviewId);
      setSuccessMessage('Review approved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReviews();
    } catch (error) {
      setError('Failed to approve review: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (reviewId, reason) => {
    if (!reason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(reviewId);
      setError('');
      await rejectReview(reviewId, reason);
      setSuccessMessage('Review rejected successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowRejectModal(false);
      setReviewToReject(null);
      setRejectionReason('');
      fetchReviews();
    } catch (error) {
      setError('Failed to reject review: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedReviews.length === 0) return;
    
    if (bulkAction === 'approve') {
      const confirmApprove = window.confirm(
        `Approve ${selectedReviews.length} selected review(s)?`
      );
      if (confirmApprove) {
        try {
          setError('');
          for (const reviewId of selectedReviews) {
            await approveReview(reviewId);
          }
          setSuccessMessage(`Successfully approved ${selectedReviews.length} review(s)!`);
          setTimeout(() => setSuccessMessage(''), 3000);
          setSelectedReviews([]);
          setBulkAction('');
          fetchReviews();
        } catch (error) {
          setError('Failed to approve some reviews: ' + error.message);
        }
      }
    } else if (bulkAction === 'reject') {
      setShowRejectModal(true);
    }
  };

  const handleBulkReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason.');
      return;
    }

    try {
      setError('');
      for (const reviewId of selectedReviews) {
        await rejectReview(reviewId, rejectionReason);
      }
      setSuccessMessage(`Successfully rejected ${selectedReviews.length} review(s)!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSelectedReviews([]);
      setBulkAction('');
      setRejectionReason('');
      setShowRejectModal(false);
      fetchReviews();
    } catch (error) {
      setError('Failed to reject some reviews: ' + error.message);
    }
  };

  const toggleSelectReview = (reviewId) => {
    setSelectedReviews(prev =>
      prev.includes(reviewId)
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedReviews.length === reviews.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(reviews.map(r => r.rating_id));
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <FiStar
        key={i}
        className={`w-3.5 h-3.5 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
        }`}
      />
    ));
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getRatingConfig = (rating) => {
    if (rating >= 4) return { color: 'text-green-400', bg: 'bg-green-900/30', icon: <FiTrendingUp /> };
    if (rating >= 3) return { color: 'text-yellow-400', bg: 'bg-yellow-900/30', icon: <FiStar /> };
    if (rating >= 2) return { color: 'text-orange-400', bg: 'bg-orange-900/30', icon: <FiTrendingDown /> };
    return { color: 'text-red-400', bg: 'bg-red-900/30', icon: <FiAlertCircle /> };
  };

  // Loading Skeleton
  if (loading && reviews.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Review</span> Moderation
            </h1>
            <p className="text-gray-400 mt-1">Manage and moderate buyer reviews before they go public</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => fetchReviews()}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={() => {/* Export functionality */}}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-blue-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Reviews</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-blue-300 transition-colors">
                {pagination.total || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                All time
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiMessageSquare className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-yellow-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Pending Reviews</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-yellow-300 transition-colors">
                {reviews.filter(r => r.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Awaiting moderation
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-green-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Approved</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-green-300 transition-colors">
                {reviews.filter(r => r.status === 'approved').length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Published reviews
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiThumbsUp className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-red-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Rejected</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-red-300 transition-colors">
                {reviews.filter(r => r.status === 'rejected').length}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Declined reviews
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiThumbsDown className="text-red-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters & Search</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Reviews</option>
                  <option value="pending">Pending Reviews</option>
                  <option value="approved">Approved Reviews</option>
                  <option value="rejected">Rejected Reviews</option>
                </select>
              </div>

              {/* Rating Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Minimum Rating</label>
                <select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange('rating', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4+ Stars</option>
                  <option value="3">3+ Stars</option>
                  <option value="2">2+ Stars</option>
                  <option value="1">1+ Stars</option>
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search reviews..."
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium hover:scale-[1.02]"
                  >
                    Search
                  </button>
                </form>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setFilters({
                    status: '',
                    rating: '',
                    dateRange: 'all',
                    page: 1,
                    limit: 20
                  });
                  setSearchTerm('');
                  setShowFilters(false);
                }}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowFilters(false);
                    fetchReviews();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedReviews.length > 0 && (
        <div className="mb-6 bg-gray-800 rounded-xl border border-gray-700 p-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiPackage className="text-[#ff660e]" />
              </div>
              <div>
                <h3 className="font-medium text-white">Bulk Actions</h3>
                <p className="text-sm text-gray-400">
                  {selectedReviews.length} review{selectedReviews.length === 1 ? '' : 's'} selected
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={bulkAction}
                onChange={(e) => {
                  setBulkAction(e.target.value);
                  if (e.target.value === 'reject') {
                    setShowRejectModal(true);
                  }
                }}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              >
                <option value="">Choose Action</option>
                <option value="approve">Approve Selected</option>
                <option value="reject">Reject Selected</option>
              </select>
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
              <button
                onClick={() => setSelectedReviews([])}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-400">
          {loading ? 'Loading...' : `Showing ${reviews.length} reviews`}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'table'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiBarChart2 />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'card'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {/* Reviews Content */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">
                    <input
                      type="checkbox"
                      checked={reviews.length > 0 && selectedReviews.length === reviews.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Review Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Buyer & Seller
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {reviews.length > 0 ? (
                  reviews.map((review) => {
                    const status = statusConfig[review.status];
                    const ratingConfig = getRatingConfig(review.rating_overall);
                    
                    return (
                      <tr key={review.rating_id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedReviews.includes(review.rating_id)}
                            onChange={() => toggleSelectReview(review.rating_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start space-x-4">
                            {review.listing_image && (
                              <img
                                src={`${IMAGE_BASE_URL}${review.listing_image}`}
                                alt={review.listing_title}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-600"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/80x80/374151/6b7280?text=No+Image';
                                }}
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <h4 className="font-medium text-white mb-1">
                                {review.listing_title || 'N/A'}
                              </h4>
                              <p className="text-sm text-gray-400 line-clamp-2">
                                {review.review_text || 'No review text provided'}
                              </p>
                              {review.seller_response && (
                                <div className="mt-2 p-2 bg-blue-900/20 rounded border border-blue-800/30">
                                  <p className="text-xs font-medium text-blue-300 mb-1">
                                    Seller Response:
                                  </p>
                                  <p className="text-xs text-blue-400">
                                    {review.seller_response}
                                  </p>
                                </div>
                              )}
                              {review.is_edited && (
                                <span className="inline-flex items-center px-2 py-1 mt-2 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                                  <FiEdit className="w-3 h-3 mr-1" />
                                  Edited
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div>
                              <div className="flex items-center space-x-2">
                                <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${ratingConfig.bg} ${ratingConfig.color}`}>
                                  {ratingConfig.icon}
                                  <span className="ml-1 font-bold">{review.rating_overall}/5</span>
                                </div>
                                <div className="flex space-x-0.5">
                                  {renderStars(review.rating_overall)}
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              {review.rating_quality && (
                                <div className="text-xs text-gray-400">
                                  Quality: <span className="font-medium text-gray-300">{review.rating_quality}/5</span>
                                </div>
                              )}
                              {review.rating_communication && (
                                <div className="text-xs text-gray-400">
                                  Communication: <span className="font-medium text-gray-300">{review.rating_communication}/5</span>
                                </div>
                              )}
                              {review.rating_delivery && (
                                <div className="text-xs text-gray-400">
                                  Delivery: <span className="font-medium text-gray-300">{review.rating_delivery}/5</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Buyer</p>
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                                  <FiUser className="w-4 h-4 text-gray-400" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {review.buyer_first_name} {review.buyer_last_name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    Order #{review.order_number || 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Seller</p>
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                                  <FiPackage className="w-4 h-4 text-gray-400" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {review.store_name || `${review.seller_first_name} ${review.seller_last_name}`}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {review.business_type || 'Individual Seller'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2 text-gray-400">
                            <FiCalendar className="w-4 h-4" />
                            <span className="text-sm">{formatDate(review.created_at)}</span>
                          </div>
                          {review.approved_at && (
                            <p className="text-xs text-green-400 mt-2">
                              Approved: {formatDate(review.approved_at)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color} ${status.border}`}>
                            {status.icon}
                            <span className="ml-1">{status.label}</span>
                          </span>
                          {review.admin_notes && (
                            <p className="text-xs text-gray-400 mt-2 line-clamp-2">
                              Note: {review.admin_notes}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={() => navigate(`/admin/reviews/${review.rating_id}`)}
                              className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                            >
                              <FiEye className="mr-1" />
                              View Details
                            </button>
                            
                            {review.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(review.rating_id)}
                                  disabled={actionLoading === review.rating_id}
                                  className="px-3 py-1.5 bg-green-900/30 hover:bg-green-900/50 text-green-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                  <FiCheckCircle className="mr-1" />
                                  {actionLoading === review.rating_id ? 'Approving...' : 'Approve'}
                                </button>
                                
                                <button
                                  onClick={() => {
                                    setReviewToReject(review);
                                    setShowRejectModal(true);
                                  }}
                                  disabled={actionLoading === review.rating_id}
                                  className="px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                  <FiXCircle className="mr-1" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-600 text-6xl mb-6">📝</div>
                      <h3 className="text-xl font-medium text-white mb-2">No reviews found</h3>
                      <p className="text-gray-400 mb-6">
                        {filters.status === 'pending' 
                          ? 'No pending reviews to moderate.'
                          : 'No reviews match your current filters.'}
                      </p>
                      <button
                        onClick={() => {
                          setFilters({
                            status: '',
                            rating: '',
                            dateRange: 'all',
                            page: 1,
                            limit: 20
                          });
                          setSearchTerm('');
                        }}
                        className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                      >
                        Clear Filters
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div className="p-6">
            {reviews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reviews.map((review) => {
                  const status = statusConfig[review.status];
                  const ratingConfig = getRatingConfig(review.rating_overall);
                  
                  return (
                    <div key={review.rating_id} className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedReviews.includes(review.rating_id)}
                            onChange={() => toggleSelectReview(review.rating_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                          {review.listing_image && (
                            <img
                              src={`${IMAGE_BASE_URL}${review.listing_image}`}
                              alt={review.listing_title}
                              className="w-12 h-12 object-cover rounded-lg border border-gray-600"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/48x48/374151/6b7280?text=No+Image';
                              }}
                            />
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                          {status.icon}
                          <span className="ml-1">{status.label}</span>
                        </span>
                      </div>
                      
                      <h4 className="font-medium text-white mb-2 group-hover:text-[#ff660e] transition-colors line-clamp-1">
                        {review.listing_title}
                      </h4>
                      
                      <div className="flex items-center space-x-2 mb-3">
                        <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${ratingConfig.bg} ${ratingConfig.color}`}>
                          {ratingConfig.icon}
                          <span className="ml-1 font-bold">{review.rating_overall}/5</span>
                        </div>
                        <div className="flex space-x-0.5">
                          {renderStars(review.rating_overall)}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                        {review.review_text || 'No review text provided'}
                      </p>
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                            <FiUser className="w-3 h-3 text-gray-400" />
                          </div>
                          <span className="text-sm text-gray-300 truncate">
                            {review.buyer_first_name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex space-x-2 pt-4 border-t border-gray-600">
                        <button
                          onClick={() => navigate(`/admin/reviews/${review.rating_id}`)}
                          className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                        {review.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(review.rating_id)}
                              disabled={actionLoading === review.rating_id}
                              className="flex-1 px-3 py-1.5 bg-green-900/30 hover:bg-green-900/50 text-green-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center disabled:opacity-50"
                            >
                              <FiCheckCircle className="mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                setReviewToReject(review);
                                setShowRejectModal(true);
                              }}
                              className="flex-1 px-3 py-1.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                            >
                              <FiXCircle className="mr-1" />
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-600 text-6xl mb-6">📝</div>
                <h3 className="text-xl font-medium text-white mb-2">No reviews found</h3>
                <p className="text-gray-400 mb-6">
                  {filters.status === 'pending' 
                    ? 'No pending reviews to moderate.'
                    : 'No reviews match your current filters.'}
                </p>
                <button
                  onClick={() => {
                    setFilters({
                      status: '',
                      rating: '',
                      dateRange: 'all',
                      page: 1,
                      limit: 20
                    });
                    setSearchTerm('');
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {reviews.length > 0 && pagination && pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-400">
              Showing {((pagination.page || 1) - 1) * (pagination.limit || 20) + 1} to {Math.min((pagination.page || 1) * (pagination.limit || 20), pagination.total || 0)} of {pagination.total || 0} reviews
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) <= 1}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="text-gray-300" />
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(Math.min(5, pagination.pages || 1)).keys()].map(i => {
                  const pageNum = i + 1;
                  const isCurrent = pageNum === (pagination.page || 1);
                  
                  let showPage = false;
                  if ((pagination.pages || 1) <= 5) {
                    showPage = true;
                  } else if (pageNum <= 2 || pageNum >= (pagination.pages || 1) - 1 || Math.abs(pageNum - (pagination.page || 1)) <= 1) {
                    showPage = true;
                  }
                  
                  if (showPage) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleFilterChange('page', pageNum)}
                        className={`w-8 h-8 rounded-lg transition-colors ${
                          isCurrent 
                            ? 'bg-[#ff660e] text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === 3 && (pagination.pages || 1) > 5) {
                    return <span key="ellipsis" className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.pages || 1)}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="text-gray-300" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {reviewToReject ? 'Reject Review' : 'Bulk Reject Reviews'}
                </h3>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setReviewToReject(null);
                    setRejectionReason('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this review is being rejected..."
                  rows="4"
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white placeholder-gray-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be shown to the buyer.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setReviewToReject(null);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (reviewToReject) {
                      handleReject(reviewToReject.rating_id, rejectionReason);
                    } else {
                      handleBulkReject();
                    }
                  }}
                  disabled={!rejectionReason.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reviewToReject ? 'Reject Review' : `Reject ${selectedReviews.length} Reviews`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default Reviews;